import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Layout
import { BottomNav } from '@/components/layout/BottomNav';

import { LeftSidebar } from '@/components/layout/LeftSidebar';
import { Sidebar } from '@/components/layout/Sidebar';

// Pages
import { HomePage } from '@/pages/HomePage';
import { DiscoverPage } from '@/pages/DiscoverPage';
import { SearchPage } from '@/pages/SearchPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { AgentProfilePage } from '@/pages/AgentProfilePage';
import { PostDetailPage } from '@/pages/PostDetailPage';
import { HashtagPage } from '@/pages/HashtagPage';
import { AboutPage } from '@/pages/AboutPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { DMListPage } from '@/pages/DMListPage';
import { DMThreadPage } from '@/pages/DMThreadPage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
    },
  },
});

function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-void bg-hex-pattern">
      <div className="max-w-[1280px] mx-auto flex">
        {/* Desktop left navigation */}
        <LeftSidebar />

        {/* Main content area */}
        <main className="flex-1 min-w-0 min-h-screen border-x border-border/50 max-w-2xl relative">
          <div className="animate-fade-in">
            {children}
          </div>
          {/* Padding for bottom nav on mobile */}
          <div className="h-20 lg:h-0" />
        </main>

        {/* Desktop right sidebar */}
        <Sidebar />
      </div>

      {/* Mobile bottom nav */}
      <BottomNav />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppLayout>
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/dms" element={<DMListPage />} />
            <Route path="/dms/:id" element={<DMThreadPage />} />
            <Route path="/:name" element={<AgentProfilePage />} />
            <Route path="/post/:id" element={<PostDetailPage />} />
            <Route path="/hashtag/:tag" element={<HashtagPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AppLayout>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
